import React, {useState} from "react";
import RichTextRenderer from "./RichText/RichTextRenderer";

interface Player {
  playerId: string;
  displayName: string;
  characterId: string;
  characterName: string;
  characterClass: string;
  characterLevel: number;
}

interface SessionCardProps {
  id: string;
  title: string;
  date: string;
  time?: string;
  duration?: string;
  location: string;
  titan: string;
  levelRange: string;
  maxPlayers: number;
  players: Player[];
  description?: string;
  isOpen?: boolean;
  onToggleOpen?: () => void;
  actionLabel?: string;
  onAction?: () => void;
  actionDisabled?: boolean;
  secondaryActionLabel?: string;
  onSecondaryAction?: () => void;
  tertiaryActionLabel?: string;
  onTertiaryAction?: () => void;
}

const SessionCard: React.FC<SessionCardProps> = ({
  title,
  date,
  time = "",
  duration = "",
  location,
  titan,
  levelRange,
  maxPlayers,
  players,
  description = "",
  isOpen = false,
  onToggleOpen,
  actionLabel,
  onAction,
  actionDisabled,
  secondaryActionLabel,
  onSecondaryAction,
  tertiaryActionLabel,
  onTertiaryAction,
}) => {
  const [showTooltip, setShowTooltip] = useState(false);

  const handleCardClick = (e: React.MouseEvent) => {
    // Don't trigger card click if clicking on buttons
    if ((e.target as HTMLElement).closest("button")) {
      return;
    }
    onToggleOpen?.();
  };

  const handleButtonClick = (e: React.MouseEvent, callback?: () => void) => {
    e.stopPropagation();
    callback?.();
  };

  const handleShareClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onTertiaryAction?.();
    setShowTooltip(true);
    setTimeout(() => setShowTooltip(false), 1500);
  };

  return (
    <div
      className={`env-character-item ${onToggleOpen ? "env-clickable-card" : ""}`}
      onClick={handleCardClick}
    >
      <div className="env-character-info">
        {/* Header with title and arrow */}
        <div className="env-card-header">
          <h3 className="env-card-title">{title}</h3>
          {onToggleOpen && (
            <button
              className="btn btn-secondary env-card-toggle"
              onClick={(e) => handleButtonClick(e, onToggleOpen)}
              aria-label={isOpen ? "Collapse" : "Expand"}
            >
              {isOpen ? "▾" : "▸"}
            </button>
          )}
        </div>

        {/* Session details with improved spacing */}
        <div className="env-card-details">
          <p className="env-character-details">
            <strong>World/Titan:</strong> {titan}
          </p>
          <p className="env-character-details">
            <strong>Date & Time:</strong> {date}
            {time && ` at ${time}`}
            {duration && ` (${duration})`}
          </p>
          <p className="env-character-details">
            <strong>Location:</strong> {location}
          </p>
          <p className="env-character-tokens">
            {levelRange} / {players.length} / {maxPlayers} players signed up
          </p>
        </div>

        {/* Action buttons */}
        {(actionLabel || secondaryActionLabel || tertiaryActionLabel) && (
          <div className="env-card-actions">
            {actionLabel && (
              <button
                className="btn btn-primary"
                onClick={(e) => handleButtonClick(e, onAction)}
                disabled={actionDisabled}
              >
                {actionLabel}
              </button>
            )}
            {secondaryActionLabel && (
              <button
                className="btn btn-secondary"
                onClick={(e) => handleButtonClick(e, onSecondaryAction)}
              >
                {secondaryActionLabel}
              </button>
            )}
            {tertiaryActionLabel && (
              <div className="share-button-container">
                <button
                  className="btn btn-secondary"
                  onClick={handleShareClick}
                >
                  {tertiaryActionLabel}
                </button>
                {showTooltip && (
                  <div className="share-tooltip">Copied to clipboard</div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Expanded content */}
        {isOpen && (
          <div className="env-card-expanded">
            <div className="env-card-section">
              <div className="env-card-section-title">Session description</div>
              <div className="env-card-section-content">
                <RichTextRenderer
                  content={description || "No description provided"}
                />
              </div>
            </div>
            <div className="env-card-section">
              <div className="env-card-section-title">Attending players</div>
              <div className="env-card-players-grid">
                {players.length > 0 ? (
                  players.map((p, i) => (
                    <div key={i} className="env-card-player-item">
                      {p.displayName} - {p.characterName} ({p.characterClass}{" "}
                      {p.characterLevel})
                    </div>
                  ))
                ) : (
                  <div className="env-card-player-item">
                    No players signed up yet
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SessionCard;
